import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ChevronRight, AlertTriangle, Calendar, MapPin } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { formatRelative, formatSurface } from "@/lib/utils";
import Logo from "@/components/Logo";

export default function PublicSchedule() {
  const { eventId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(localStorage.getItem(`dh-public-${eventId}`) || null);

  useEffect(() => {
    (async () => {
      try {
        const { data: res } = await api.get(`/public/event/${eventId}`);
        setData(res);
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail) || "Erreur");
      }
    })();
    const interval = setInterval(async () => {
      try {
        const { data: res } = await api.get(`/public/event/${eventId}`);
        setData(res);
      } catch {}
    }, 30000);
    return () => clearInterval(interval);
  }, [eventId]);

  if (error) {
    return (
      <Centered>
        <AlertTriangle size={40} strokeWidth={2} className="mx-auto mb-3 text-[var(--muted)]" />
        <h2 className="font-serif text-3xl mb-2">Horaire introuvable</h2>
        <p className="text-sm text-[var(--muted)]">Le lien est invalide ou l'horaire n'a pas encore été publié.</p>
      </Centered>
    );
  }
  if (!data) return <Centered><p className="font-mono text-xs uppercase tracking-widest text-[var(--muted)] alert-pulse">Chargement…</p></Centered>;

  const event = data.event;
  const officials = data.officials || [];

  if (!selected) {
    return (
      <OfficialPicker
        event={event}
        officials={officials}
        onPick={(id) => {
          localStorage.setItem(`dh-public-${eventId}`, id);
          setSelected(id);
        }}
      />
    );
  }

  const me = officials.find((o) => o.id === selected);
  const myGames = (event.games || [])
    .filter((g) => !g.is_break && (g.referee1_id === selected || g.referee2_id === selected || g.scorer_id === selected))
    .sort((a, b) => (a.date || "").localeCompare(b.date || "") || a.start_time.localeCompare(b.start_time));

  const myRole = (g) => {
    if (g.referee1_id === selected) return "Arbitre 1";
    if (g.referee2_id === selected) return "Arbitre 2";
    if (g.scorer_id === selected) return "Marqueur";
    return "";
  };

  return (
    <div className="min-h-screen bg-[#F5F5F0] p-4 md:p-8" data-testid="public-schedule-page">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <Logo size={36} />
        </div>

        <div className="card-flat mb-6">
          <div className="label mb-1">Bonjour</div>
          <h1 className="font-serif text-4xl leading-tight mb-3">
            {me?.first_name} {me?.last_name}
          </h1>
          <div className="font-mono text-xs text-[var(--muted)] flex flex-wrap gap-3">
            <span className="inline-flex items-center gap-1"><Calendar size={12} strokeWidth={2.5} />{event.date}</span>
            <span>{event.name}</span>
            {event.location && <span className="inline-flex items-center gap-1"><MapPin size={12} strokeWidth={2.5} />{event.location}</span>}
          </div>
          <p className="mt-3 text-xs text-[var(--muted)] border-l-4 border-black pl-3 italic">
            💡 Pour voir tes parties dans toutes les éditions, crée-toi un compte via le lien d'inscription de ton gestionnaire (<a href="/officiel/login" className="underline">/officiel/login</a>).
          </p>
          <button
            onClick={() => {
              localStorage.removeItem(`dh-public-${eventId}`);
              setSelected(null);
            }}
            className="btn-ghost mt-3 !px-0"
          >
            Ce n'est pas moi
          </button>
        </div>

        {myGames.length === 0 ? (
          <div className="border-2 border-dashed border-[var(--border-strong)] p-12 text-center bg-white">
            <p className="font-serif text-2xl italic mb-2">Pas d'affectation</p>
            <p className="font-mono text-xs uppercase tracking-widest text-[var(--muted)]">
              Tu n'es assigné à aucune partie pour cet événement.
            </p>
          </div>
        ) : (
          <>
            <div className="label mb-3">
              Mes {myGames.length} partie{myGames.length > 1 ? "s" : ""}
            </div>
            <div className="space-y-3">
              {myGames.map((g) => (
                <div key={g.id} className="card-flat" data-testid={`my-game-${g.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-serif text-3xl tabular leading-none">
                      {g.start_time} <span className="text-[var(--muted-2)]">→</span> {g.end_time}
                    </div>
                    <span className="badge-dark">{myRole(g)}</span>
                  </div>
                  <div className="font-mono text-sm">
                    <b>{g.team_a || "Équipe A"}</b> <span className="text-[var(--muted-2)]">vs</span> <b>{g.team_b || "Équipe B"}</b>
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] mt-2 flex gap-3 flex-wrap">
                    <span>Surface {formatSurface(g.surface)}</span>
                    {g.category && <span>{g.category}</span>}
                    {g.date && <span>{g.date}</span>}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <p className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] mt-8 text-center">
          Publié {formatRelative(event.published_at)} · Rafraîchit auto.
        </p>
      </div>
    </div>
  );
}

function OfficialPicker({ event, officials, onPick }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(
    () =>
      officials.filter((o) =>
        (o.first_name + " " + o.last_name).toLowerCase().includes(q.toLowerCase())
      ),
    [q, officials]
  );
  return (
    <div className="min-h-screen bg-[#F5F5F0] p-4 md:p-8 flex items-center justify-center">
      <div className="max-w-lg w-full">
        <div className="mb-6">
          <Logo size={36} />
        </div>
        <div className="card-flat">
          <div className="label mb-1">{event.name}</div>
          <h2 className="font-serif text-4xl mb-2">Qui es-tu?</h2>
          <p className="text-sm text-[var(--muted)] mb-5 leading-relaxed">
            Sélectionne ton nom pour voir ton horaire personnel. Tu as un compte? Connecte-toi via <a href="/officiel/login" className="underline text-black">/officiel/login</a>.
          </p>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher…"
            className="input-box mb-3"
            data-testid="official-search-input"
          />
          <div className="border-t border-[var(--border)] max-h-80 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="text-xs text-[var(--muted)] py-4 text-center">Aucun résultat</p>
            ) : (
              filtered.map((o) => (
                <button
                  key={o.id}
                  onClick={() => onPick(o.id)}
                  className="w-full text-left py-3 px-2 border-b border-[var(--border)] hover:bg-[var(--surface-3)] flex items-center justify-between group"
                  data-testid={`pick-official-${o.id}`}
                >
                  <span>
                    <span className="font-bold text-sm">
                      {o.first_name} {o.last_name}
                    </span>
                  </span>
                  <ChevronRight size={14} strokeWidth={2.5} className="group-hover:translate-x-1 transition-transform" />
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Centered({ children }) {
  return (
    <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center p-6">
      <div className="text-center max-w-md">{children}</div>
    </div>
  );
}
